import { FC } from "react";
import { useSelector } from "react-redux";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiLink,
  MuiTypography,
} from "../../../../../../components";
import MuiEllipseLabel from "../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import {
  getValueOrBlank,
  getValueOrDash,
} from "../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { programStatusSelector } from "../../../../../../redux/slices/program/programSelector";
import { ProgramDetail } from "../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

interface KeyDetailProps {
  programDetail?: ProgramDetail;
  displayKeyDetails: boolean;
  isFromEditProjectChildPrograms?: boolean;
  onNavigateToProjectCallBack?: (projectId: string) => void;
}

export const KeyDetail: FC<KeyDetailProps> = ({
  programDetail,
  displayKeyDetails,
  isFromEditProjectChildPrograms,
  onNavigateToProjectCallBack,
}) => {
  const programStatus = useSelector(programStatusSelector);
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.generalDetails, key);
  };

  return (
    <MuiBox>
      <MuiGrid
        container
        data-testid="key-detail-tab"
        className="key-detail-tab"
      >
        {displayKeyDetails && (
          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            data-testid="key-number"
          >
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.programKey)}
              value={programDetail?.keyNumber}
              isValueWithEllipsis
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalClient)}
            value={programDetail?.clientName}
            isValueWithEllipsis
          />
          <MuiTypography
            component="h4"
            className="value-style"
            variant="h4Bold"
          >
            {programDetail?.clientCode}
          </MuiTypography>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalDeliveryCountry)}
            value={programDetail?.deliveryCountryName}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalProductLine)}
            value={programDetail?.lineOfBusiness}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalProgramTemplate)}
            value={programDetail?.programTemplate}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}></MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalOpportunityID)}
            value={programDetail?.opportunityId}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalOpportunityName)}
            value={programDetail?.opportunityName}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalAlternateDisplayName)}
            value={programDetail?.alternateDisplayName}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalSalesOffice)}
            value={programDetail?.salesOffice}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={fetchLabel(
              programSiteCoreKeys.keyProgramDetails,
              programSiteCoreKeys.nextPersona,
            )}
            value={programDetail?.nextPersona}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.generalProgramStatus)}
            value={""}
          />
          <StatusBadge
            status={programStatus as StatusBadgeType}
            label={fetchValueByKeyOfDropdown(
              programSiteCoreKeys.generalDetails,
              programSiteCoreKeys.statusList,
              programStatus as StatusBadgeType,
            )}
          />
        </MuiGrid>

        {programDetail?.isProjectTagged && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              {displayKeyDetails && isFromEditProjectChildPrograms ? (
                <MuiDoubleLabel
                  label={getLabel(programSiteCoreKeys.generalProjectKey)}
                  value={programDetail?.projectKey}
                  isValueWithEllipsis
                  replaceEmptyWithDash
                />
              ) : (
                <>
                  <MuiEllipseLabel
                    text={getLabel(programSiteCoreKeys.generalProjectKey)}
                  />

                  <MuiLink>
                    <MuiEllipseLabel
                      labelTypoVariant="h3Bold"
                      onClick={() =>
                        onNavigateToProjectCallBack?.(
                          getValueOrBlank(programDetail?.projectId),
                        )
                      }
                      text={programDetail?.projectKey}
                      pointerCursor
                    />
                  </MuiLink>
                </>
              )}
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              {displayKeyDetails && isFromEditProjectChildPrograms ? (
                <MuiDoubleLabel
                  label={getLabel(programSiteCoreKeys.generalProjectName)}
                  value={programDetail?.projectName}
                  isValueWithEllipsis
                  replaceEmptyWithDash
                />
              ) : (
                <>
                  <MuiEllipseLabel
                    text={getLabel(programSiteCoreKeys.generalProjectName)}
                  />

                  <MuiLink>
                    <MuiEllipseLabel
                      labelTypoVariant="h3Bold"
                      onClick={() =>
                        onNavigateToProjectCallBack?.(
                          getValueOrBlank(programDetail?.projectId),
                        )
                      }
                      text={getValueOrDash(programDetail?.projectName)}
                      pointerCursor
                    />
                  </MuiLink>
                </>
              )}
            </MuiGrid>
          </>
        )}
      </MuiGrid>
    </MuiBox>
  );
};
